import React, { useEffect } from 'react';

import dayjs from 'dayjs';
import styled from 'styled-components';

import Checkbox from 'components/lib/Checkbox';
import { EMAIL_NOTIFICATION_TYPE } from 'components/Settings/constants';

import { getTabDataOrDefault, useShareViaEmailContext } from '../ShareViaEmailContext';

const Sidebar = styled.div`
  width: 219px;
  min-height: 550px;
  padding-top: 32px;
  border-right: solid 1px var(--line-gray);
  font-size: 12px;
  padding-left: 30px;
  background-color: var(--background-gray-light);
`;

const Title = styled.h3`
  font-size: 16px;
`;

const Paragraph = styled.p`
  margin-bottom: 1px;
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 15px;
`;

const StyledCheckbox = styled(Checkbox)`
  display: flex;
  margin-bottom: 10px;

  .ant-checkbox-label {
    display: flex;
  }

  .ant-checkbox-input {
    width: 18px;
    height: 18px;
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: var(--positive-green);
    border-color: var(--positive-green);
  }
`;

const CompetitorDetails = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.2;
  font-size: 12px;
  font-weight: 500;
  margin-left: 5px;
`;

const ViewsList = styled.div`
  display: flex;
  flex-direction: column;
`;

const EmptyState = styled.p`
  font-size: 12px;
  color: var(--negative-orange);
  margin: 8px 0 0;
`;

interface Props {
  competitors: any[];
}

const SendEmailNotificationSidebar = ({ competitors }: Props) => {
  const { notificationsTabData, notificationType, updateNotificationTabData } = useShareViaEmailContext();
  const tabData = getTabDataOrDefault(notificationsTabData?.[notificationType]);

  const selectedCompetitors = tabData.selectedCompetitors ?? [];

  const hasCompetitors = Boolean(competitors?.length);

  useEffect(() => {
    if (!competitors || competitors.length === 0) return;

    if (!selectedCompetitors || selectedCompetitors.length === 0) {
      let defaultSelected = competitors.map((c) => c.id);

      if (
        notificationType === EMAIL_NOTIFICATION_TYPE.MARKET_INTELLIGENCE_DIGEST ||
        notificationType === EMAIL_NOTIFICATION_TYPE.SALES_BATTLECARD_DIGEST
      ) {
        const now = dayjs();
        defaultSelected = competitors
          .filter((c) => c.latest_snapshot_date && now.diff(dayjs(c.latest_snapshot_date), 'month') < 3)
          .map((c) => c.id);
      }

      updateNotificationTabData(notificationType, { selectedCompetitors: defaultSelected });
    }
  }, [competitors, notificationType]);

  const handleCompetitorSelect = (id: number) => {
    const updated = selectedCompetitors.includes(id)
      ? selectedCompetitors.filter((x) => x !== id)
      : [...selectedCompetitors, id];

    updateNotificationTabData(notificationType, { selectedCompetitors: updated });
  };

  return (
    <Sidebar>
      <Title>Filters</Title>
      <Paragraph>Competitors</Paragraph>
      <ViewsList>
        {!hasCompetitors && <EmptyState>No Competitors</EmptyState>}
        {(competitors ?? []).map((item) => {
          const hasSnapshotDate = Boolean(item.latest_snapshot_date);
          const snapshotLabel = hasSnapshotDate
            ? `Updated ${dayjs(item.latest_snapshot_date).format('MMM YYYY')}`
            : 'No data available';
          const snapshotColor = hasSnapshotDate ? 'var(--text-secondary)' : 'var(--negative-orange)';

          return (
            <StyledCheckbox
              key={item.id}
              checked={selectedCompetitors.includes(item.id)}
              onChange={() => handleCompetitorSelect(item.id)}
              disabled={
                !hasSnapshotDate &&
                (notificationType === EMAIL_NOTIFICATION_TYPE.MARKET_INTELLIGENCE_DIGEST ||
                  notificationType === EMAIL_NOTIFICATION_TYPE.SALES_BATTLECARD_DIGEST)
              }
            >
              <CompetitorDetails>
                <span>{item.comunity_name}</span>
                {(notificationType === EMAIL_NOTIFICATION_TYPE.MARKET_INTELLIGENCE_DIGEST ||
                  notificationType === EMAIL_NOTIFICATION_TYPE.SALES_BATTLECARD_DIGEST) && (
                  <span style={{ color: snapshotColor, fontSize: '10px' }}>{snapshotLabel}</span>
                )}
              </CompetitorDetails>
            </StyledCheckbox>
          );
        })}
      </ViewsList>
    </Sidebar>
  );
};

export default SendEmailNotificationSidebar;
